import { FC, useState } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { Button } from '@new/common/ui/Button/Button';
import { ErrorMessage } from '@new/common/ui/ErrorMessage/ErrorMessage';
import { useCheckCouponMutation } from '@teachur/http-client';
import { getErrorMessage } from '@new/lib';

interface CouponModalProps {
  isOpen: boolean;
  initialValue: string;
  onClose: () => void;
  /** Called with the applied code + recalculated prices when the coupon is valid. */
  onApplied: (code: string, newPrice: number, newMonthlyPrice: number) => void;
}

export const CouponModal: FC<CouponModalProps> = ({ isOpen, initialValue, onClose, onApplied }) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: checkCoupon, isPending } = useCheckCouponMutation();

  const handleApply = async () => {
    const code = value.trim();
    setError(null);

    if (!code) {
      setError('Please enter a code.');
      return;
    }

    try {
      const result = await checkCoupon(code);

      if (!result.valid) {
        setError('Your code is not valid.');
        return;
      }

      // Valid coupon: propagate prices, then close.
      onApplied(code, result.newPrice, result.newMonthlyPrice);
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'Wrong code.'));
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Enter code" className="max-w-md">
      <div className="flex flex-col gap-4">
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Code"
          className="border-2! border-(--neutral-white-NW800)! rounded-xl! p-4 outline-none"
        />

        {error && <ErrorMessage errors={[error]} />}

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>

          <Button type="button" variant="primary" onClick={handleApply} disabled={isPending}>
            {isPending ? 'Checking...' : 'Apply'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
